import { CachedItem } from '@DStvMonoRepo/self-service-core';
import { action, computed, makeObservable, observable } from 'mobx';
import i18n from '../i18n/i18n';
import { routes as ROUTES } from './app.routes';
import { BaseRoute } from './core/models/base-routes';
import { AuthStore } from './core/stores/auth.store';
import { Util } from './utils/common.utils';
import { PaymentPlatform, PaymentStore } from './core/stores/payment/payment.store';
import { AuthService } from './core/services/auth';
import { PackageStore } from './core/stores/package.store';
import { environment } from '../environments/environment';
import { StorageHelper } from './core/services/storage';
import { CacheAreaKey } from './core/config/cache-config';
import { AnalyticsStore } from './core/stores/analytics.store';
import { AppLoadStep } from './core/enums/analytics-journey-key';
import { RouteType } from './core/enums/route-type';

export interface AppInitConfig {
  packageId?: string;
  connectStore?: {
    accessToken: string;
    expiresIn: string;
    idToken: string;
    tokenType: string;
    expiresTime: number;
  };
}

export class AppStore {
  title = 'DStv';
  headerTitle?: string;
  background: string | null;
  backgroundOptions!: {
    grayscale: boolean;
    additionalOptions?: {
      backgroundPosition?: string;
      backgroundAttachment?: string;
      backgroundSize?: string;
    };
    opacity?: number;
  };
  activeStep = 1;
  navigate: {
    canNavigate: boolean;
    navigateToPrevious: boolean;
    disableActions?: boolean;
    hideProgress?: boolean;
  } = {
    canNavigate: true,
    navigateToPrevious: false,
    disableActions: false,
    hideProgress: false,
  };
  defaultBackground: string;
  locale = localStorage.getItem('ci.app-culture') || localStorage.getItem('i18nextLng') || 'en-za';
  currencyCode!: string;
  availableRoutes: BaseRoute[] = [];
  menuItems: BaseRoute[] = [];
  routes!: { menuItems: BaseRoute[]; availableRoutes: BaseRoute[] };

  activeRoute!: BaseRoute;
  isPageLoading = true;
  init = false;

  selectedPackage = StorageHelper.GetInstance.getFromCache('conf', 'package', CacheAreaKey.Config) as string;
  selectedPackages =
    (StorageHelper.GetInstance.getFromCache('conf', 'packages', CacheAreaKey.Config) as string[]) || [];
  selectedPackageName = '';
  activeTheme = StorageHelper.GetInstance.getFromCache('conf', 'theme', CacheAreaKey.Config) as string || 'dark';
  authStore = new AuthStore();
  paymentStore = new PaymentStore();
  packageStore = new PackageStore();

  allowRecurringPayments = true;
  allowMonthToMonthPayments = true;
  isDiscovery = false;

  private readonly cachedUrl = new CachedItem('app-entry', true);

  get isCapitec() {
    const capitec = StorageHelper.GetInstance.getFromCache('conf', 'isCapitec', CacheAreaKey.Config) as string;
    if (capitec?.toLowerCase() == 'true') {
      return true;
    }

    return false;
  }

  get countryCode() {
    return this.locale?.split('-')[1]?.toLowerCase() || 'za';
  }

  get lang() {
    return this.locale?.split('-')[0] || 'en';
  }

  get isLoading() {
    return (
      this.isPageLoading ||
      !this.authStore ||
      this.authStore.isLoading ||
      !this.packageStore ||
      this.packageStore.isLoading
    );
  }

  get user() {
    return this.authStore.customerDetail;
  }

  get customerNumber() {
    return this.user?.customerNumber;
  }

  get isAuthorized() {
    return this.authStore.isAuthorized;
  }

  get isAuthenticated() {
    return this.authStore.isAuthenticated;
  }

  get packageState() {
    return this.packageStore.state;
  }

  constructor() {
    this.background = null;
    this.defaultBackground = Util.GetImageAsset(`page-background-desktop.jpg`);

    StorageHelper.GetInstance.setCache('conf', { locale: Util.LocaleFromUrl() }, CacheAreaKey.Config, 'locale');

    localStorage.setItem('ci.app-culture', Util.LocaleFromUrl()); // NOTE: this should likely be reevaluated as it's likely not required for this application.

    makeObservable(this, {
      title: observable,
      activeStep: observable,
      navigate: observable,
      selectedPackageName: observable,
      background: observable,
      backgroundOptions: observable,
      defaultBackground: observable,
      locale: observable,
      menuItems: observable,
      availableRoutes: observable,
      routes: observable,
      activeRoute: observable,
      isPageLoading: observable,
      currencyCode: observable,
      paymentStore: observable,
      selectedPackage: observable,
      selectedPackages: observable,
      headerTitle: observable,
      setRoutes: action,
      changeTitle: action,
      changeActiveStep: action,
      changeNavigate: action,
      changeLocale: action,
      changeAvailableRoutes: action,
      changeRoutes: action,
      changeActiveRoute: action,
      changeCurrencyCode: action,
      changePageIsLoading: action,
      changeSelectedPackage: action,
      changeSelectedPackageName: action,
      changeSelectedPackages: action,
      changeHeaderTitle: action,
      isLoading: computed,
    });
  }

  initialise(config?: AppInitConfig) {
    if (this.init) {
      return;
    }
    this.init = true;
    this.authStore.initialise(config?.connectStore).then((_) => {
      if (!this.selectedPackage && !this.selectedPackages) {
        // TODO: navigate back to origin
        window.location.assign(`${window.location.origin}${window.location.pathname}#/`);
        return;
      }

      if (!this.isAuthorized) {
        // NOTE: confirm-package is the app entry point and should handle where the user goes.
        console.warn('User is not authenticated.');
        AnalyticsStore.addJourneyEvent(AppLoadStep.Authorized, { isAuthorized: this.isAuthorized });

        if (this.selectedPackage) {
          if (this.isCapitec) {
            AuthService.signUp(`/confirm-package/${this.selectedPackage}?isCapitec=${this.isCapitec}`);
            return;
          }

          AuthService.signUp(`/confirm-package/${this.selectedPackage}`);
          return;
        }
      }

      if (this.isAuthorized && !this.isAuthenticated) {
        AnalyticsStore.initialize(this.authStore.customerDetail, this.locale, '', this.countryCode).then((_) => {
          AnalyticsStore.addJourneyEvent(AppLoadStep.Authorized, { isAuthorized: this.isAuthorized });
        });
      }

      if (this.isAuthenticated) {
        const customerNumber = this.authStore.customerNumber;
        const tempCountry = environment.DEFAULT_COUNTRY_NAME;
        const tempLang = environment.DEFAULT_COUNTRY;
        const tempCurrencyCode = 'ZAR';
        AnalyticsStore.initialize(
          this.authStore.customerDetail,
          this.locale,
          this.customerNumber,
          this.countryCode
        ).then((_) => {
          AnalyticsStore.addJourneyEvent(AppLoadStep.Authorized, { isAuthorized: this.isAuthorized });
          AnalyticsStore.addJourneyEvent(AppLoadStep.Authenticated, {
            customerNumber: customerNumber,
          });
        });
        this.packageStore.initialise(tempCountry, customerNumber);
        this.paymentStore.initialise(
          tempCountry,
          customerNumber,
          tempCurrencyCode,
          tempLang,
          PaymentPlatform.PayAtRedirect,
          this.isDiscovery
        );
      }

      this.changeRoutes(this.setRoutes());
      this.changePageIsLoading(false);
    });
  }

  reinitialiseBaseServices = (excludePaymentStore = false) => {
    if (this.isAuthenticated) {
      const customerNumber = this.authStore.customerNumber;
      const tempCountry = environment.DEFAULT_COUNTRY_NAME;
      const tempLang = environment.DEFAULT_COUNTRY;
      const tempCurrencyCode = 'ZAR';

      this.packageStore.initialise(tempCountry, customerNumber);
      AnalyticsStore.isInitialized = false;
      AnalyticsStore.initialize(this.authStore.customerDetail, this.locale, customerNumber, this.countryCode);

      if (!excludePaymentStore) {
        this.paymentStore.initialise(
          tempCountry,
          customerNumber,
          tempCurrencyCode,
          tempLang,
          PaymentPlatform.PayAtRedirect,
          this.isDiscovery
        );
      }
    }
  };

  changeTitle(activeRoute?: BaseRoute) {
    const title = 'DStv';
    if (activeRoute?.type && activeRoute?.type === RouteType.Select) {
      this.title = `${title} Select`;
      return;
    }

    this.title = `${title} Stream`;
  }

  changeActiveStep = (step: number) => {
    this.activeStep = step;
  };

  changeNavigate = (navigate: {
    canNavigate: boolean;
    navigateToPrevious: boolean;
    disableActions?: boolean;
    hideProgress?: boolean;
    displayBackButton?: boolean;
  }) => {
    this.navigate = navigate;
  };

  disableRecurringPayments() {
    this.allowRecurringPayments = false;
  }

  disableMonthToMonthPayments() {
    this.allowMonthToMonthPayments = false;
  }

  changeDiscoveryPaymentState(state: boolean) {
    this.isDiscovery = state;
  }

  changeCapitecState(state: string) {
    StorageHelper.GetInstance.setCache('conf', state as any, CacheAreaKey.Config, 'isCapitec');
  }

  changeSelectedTheme(theme: string) {
    StorageHelper.GetInstance.setCache('conf', theme as any, CacheAreaKey.Config, 'theme');
  }

  changeSelectedPackageName = (description: string) => {
    this.selectedPackageName = description;
  };

  changeSelectedPackage(selectedPackage: string) {
    StorageHelper.GetInstance.setCache('conf', selectedPackage?.toUpperCase() as any, CacheAreaKey.Config, 'package');
    this.selectedPackage = selectedPackage?.toUpperCase();
  }

  changeSelectedPackages = (selectedPackages: string[]) => {
    StorageHelper.GetInstance.setCache('conf', selectedPackages as any, CacheAreaKey.Config, 'packages');
    this.selectedPackages = selectedPackages;
  };

  changeRoutes(routes: { menuItems: BaseRoute[]; availableRoutes: BaseRoute[] }) {
    this.routes = routes;
  }

  changeActiveRoute(name: string | null) {
    this.activeRoute = this.routes.availableRoutes.find((r) => r.name === name) as BaseRoute;
    const parent = this.activeRoute?.parent?.name;
    this.routes = {
      availableRoutes: this.routes.availableRoutes,
      menuItems: this.routes.menuItems.map((route) => ({
        ...route,
        isActive: parent === route.name || route.name === name,
      })),
    };

    if (window.location.href === this.cachedUrl.get()?.href || this.cachedUrl.get()?.hash?.length < 5) {
      this.cachedUrl.clear();
    }
    this.cachedUrl.clear();
  }

  changeLocale(locale = '') {
    this.locale = locale.toLowerCase();
    i18n.changeLanguage(this.locale);
  }

  changeCurrencyCode(currency = '') {
    this.currencyCode = currency;
  }

  changeAvailableRoutes(routes: BaseRoute[]) {
    this.availableRoutes = routes;
  }

  canActivate(isProtected?: boolean | 'Authorize') {
    return isProtected === 'Authorize'
      ? () => this.isAuthorized
      : isProtected === true
      ? () => this.isAuthenticated
      : undefined;
  }

  setRoutes() {
    return this.getEligibleRoutes(ROUTES).reduce(
      (prev: { menuItems: BaseRoute[]; availableRoutes: BaseRoute[] }, route: BaseRoute) => {
        const { children, ...baseRoute } = route;
        const childRoutes =
          children?.map((child) => ({
            ...child,
            parent: baseRoute,
            canActivate: this.canActivate(route.protected || baseRoute.protected),
          })) || [];
        const menuChildRoutes =
          children?.map((child) => ({
            ...child,
            parent: baseRoute,
            canActivate: this.canActivate(route.protected || baseRoute.protected),
          })) || [];
        const routeWithPermission: any = [
          { ...route, children: menuChildRoutes, canActivate: this.canActivate(route.protected) },
        ];

        return {
          availableRoutes: [...prev.availableRoutes, ...routeWithPermission, ...childRoutes],
          menuItems: [...prev.menuItems, ...routeWithPermission],
        };
      },
      { menuItems: [], availableRoutes: [] }
    );
  }

  getEligibleRoutes(routes: BaseRoute[]) {
    if (environment.DISABLE_SELECT) {
      return routes.filter((route) => route.type !== RouteType.Select && route.type !== RouteType.Common);
    }

    return routes;
  }

  changePageIsLoading(isLoading: boolean) {
    this.isPageLoading = isLoading;
  }

  changeHeaderTitle = (title?: string) => {
    this.headerTitle = title;
  };

  // NOTE: navigates user to .co.za self service with cross domain auth (as required).
  navigateToSelfService = () => {
    window.location.assign(environment.RESIDENTIAL_SELF_SERVICE);
  };

  navigateToStreamLanding = () => {
    if (this.isCapitec) {
      window.location.assign(`${environment.CAPITEC_LANDING_PAGE}`);
      return;
    }
    window.location.assign(`${environment.BUY_LANDING_PAGE}`);
  };

  navigateToFlexLanding = () => {
    window.location.assign(`${environment.FLEX_BUY_LANDING_PAGE}`);
  };

  // NOTE: navigates user to .co.za self service with cross domain auth (as required).
  navigateToBoxoffice = () => {
    const tokenString =
      this.user?.access_token && this.user?.id_token
        ? `?access_token=${this.user?.access_token}&id_token=${this.user?.id_token}`
        : '';
    window.location.assign(`${environment.BOXOFFICE}${this.isAuthorized ? tokenString : ''}`);
  };
}
